import {useMemo, useState} from "react";

/**
 * A hook to use when rendering dynamic lists or sets of items that don't have obvious
 * identifiers built into the data.
 *
 * NOTE: This hook uses Array.at, which may require a polyfill at time of
 *       writing.  It is polyfilled by default in NextJS.  If using without Next,
 *       please verify that the function has been polyfilled.
 * @param initialLength The number of keys to start with.
 * @returns A tuple of the key array and handler containing functions push, pop,
 *          removeAt, and setArray
 */
const useKeyArray: (
	opts: {initialLength: number} | {initialValues: string[]}
) => [
	number[],
	{
		push: () => void;
		pop: () => void;
		removeAt: (value: number) => void;
		setArray: (values: number[]) => void;
	},
] = (opts) => {
	let initialArray: number[] = [];
	if ("initialLength" in opts) {
		initialArray = [...Array(opts.initialLength).keys()];
	}

	if ("initialValues" in opts) {
		initialArray = opts.initialValues
			.filter((val) => !isNaN(parseInt(val)))
			.map((value) => parseInt(value))
			.sort();

		if (initialArray.length !== Object.keys(opts.initialValues).length) {
			console.warn("useKeyArray attempted to initialize off non-numeric keys");
		}
	}
	const [array, setArray] = useState<number[]>(initialArray);

	const handlers = useMemo(
		() => ({
			push: () => {
				setArray((current) => {
					const lastVal = current.at(-1);
					return current.concat(lastVal === undefined ? 0 : lastVal + 1);
				});
			},
			pop: () => {
				setArray((current) => current.slice(0, -1));
			},
			removeAt: (index: number) => {
				setArray((current) => [
					...current.slice(0, index),
					...current.slice(index + 1),
				]);
			},
			setArray: (arr: number[]) => {
				setArray(arr);
			},
		}),
		[]
	);

	return [array, handlers];
};

export default useKeyArray;
